import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'

import type { ApiVersion } from 'constants/api'
import { API_BASE_URI } from 'constants/api'

import type { Obj } from 'types/common'

const config: AxiosRequestConfig = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=UTF-8',
  },
  withCredentials: true,
}

export const GET = (
  url: string,
  options: AxiosRequestConfig = {},
  version: ApiVersion = 'v1',
) =>
  axios
    .get(`${API_BASE_URI(version)}${url}`, { ...config, ...options })
    .then((res) => res.data)
    .catch(handleError)

export const POST = (
  url: string,
  data: Obj,
  options: AxiosRequestConfig = {},
  version: ApiVersion = 'v1',
) =>
  axios
    .post(`${API_BASE_URI(version)}${url}`, data, { ...config, ...options })
    .then((res) => res?.data || res)
    .catch(handleError)

export const PUT = (
  url: string,
  data: Obj,
  options: AxiosRequestConfig = {},
  version: ApiVersion = 'v1',
) =>
  axios
    .put(`${API_BASE_URI(version)}${url}`, data, { ...config, ...options })
    .then((res) => res?.data || res)
    .catch(handleError)

export const PATCH = (
  url: string,
  data: Obj,
  options: AxiosRequestConfig = {},
  version: ApiVersion = 'v1',
) =>
  axios
    .patch(`${API_BASE_URI(version)}${url}`, data, { ...config, ...options })
    .then((res) => res?.data || res)
    .catch(handleError)

export const DELETE = (
  url: string,
  data?: Obj,
  options: AxiosRequestConfig = {},
  version: ApiVersion = 'v1',
) =>
  axios
    .delete(`${API_BASE_URI(version)}${url}`, { ...config, ...options, data })
    .then((res) => res?.data || res)
    .catch(handleError)

// Error thrown from a parsed API error body, carrying the HTTP status so
// downstream consumers (e.g. sentryBeforeSend) can distinguish an expected
// 404 from a raw err.message leaked by a 500.
export type ApiError = Error & { httpStatus?: number }

const errorWithStatus = (message: string, status?: number): ApiError => {
  const error: ApiError = new Error(message)
  if (status !== undefined) error.httpStatus = status
  return error
}

export const handleError = (err: unknown): never => {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status
    const data = err.response?.data as Record<string, unknown> | undefined
    if (data?.error) {
      console.error(data.error)
      throw errorWithStatus(
        typeof data.error === 'string'
          ? data.error
          : JSON.stringify(data.error),
        status,
      )
    }

    if (data?.errors) {
      console.error(data.errors)
      throw errorWithStatus(
        typeof data.errors === 'string'
          ? data.errors
          : JSON.stringify(data.errors),
        status,
      )
    }

    // Always re-throw the original AxiosError so callers see a failure
    throw err
  } else {
    console.error(err)
    throw err
  }
}

export const extrasFlags = { params: { extras: true } }

export * as bomConfig from './bomConfig'
export * as cmsPage from './cmsPage'
export * as cable from './cable'
export * as cablePrices from './cablePrices'
export * as challenge from './challenge'
export * as cloudloop from './cloudloop'
export * as logger from './logger'
export * as loggerActivation from './loggerActivation'
export * as loggerFirmware from './loggerFirmware'
export * as modem from './modem'
export * as note from './note'
export * as notification from './notification'
export * as notificationNote from './notificationNote'
export * as order from './order'
export * as org from './org'
export * as perm from './perm'
export * as project from './project'
export * as reading from './reading'
export * as reportInstance from './reportInstance'
export * as reportConfiguration from './reportConfiguration'
export * as reportTemplate from './reportTemplate'
export * as search from './search'
export * as sensor from './sensor'
export * as serviceManager from './service'
export * as omnidots from './omnidots'
export * as orion from './orion'
export * as site from './site'
export * as user from './user'
export * as tosAcceptance from './tosAcceptance'
export * as forecastRequest from './forecastRequest'
export * as forecastSubscription from './forecastSubscription'
export * as siteForecastVisibility from './siteForecastVisibility'
export * as seaIceForecast from './seaIceForecast'
export * as service from './service'
export * as contactSubmission from './contactSubmission'
export * as webhookEvent from './webhookEvent'
export * as userLanding from './userLanding'
export * as reconciliation from './reconciliation'
export * as vendorDevices from './vendorDevices'
